// The Users page's family card (#1459): what the user's household holds, for support. adminGetFamilyDetails reads it
// through the Admin SDK (the rules give an admin nothing under families/ but the subscription documents); this module
// only draws it. Pure functions, so tests/admin/familyDetailsPanel.test.js can check the markup without a browser.

import { BADGE, BADGE_DOT, BUTTON, SEGMENT, TABLE, checkbox } from "./ui/classes.js";
import { icon, platformLogo, solidIcon } from "./ui/icons.js";

const CARD = "rounded-lg bg-white shadow-xs outline-1 outline-black/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10";
const MUTED = "text-sm text-gray-500 dark:text-gray-400";
const MONO = "font-mono text-xs text-gray-500 dark:text-gray-400";

export const FAMILY_TABS = [
  { id: "plan", label: "Meal plan" },
  { id: "groceries", label: "Groceries" },
  { id: "recipes", label: "Recipes" },
  { id: "imports", label: "Imports" },
  { id: "household", label: "Household" }
];

// source_type as the apps label it (iOS RecipeSourceBadgeView). Every type but a hand-typed one is an import.
export const SOURCE_LABELS = {
  web_url: "Website",
  url: "Website",
  web: "Website",
  photo_scan: "Photo scan",
  camera: "Camera",
  photo_library: "Photo library",
  document_scan: "Document scan",
  text: "Pasted text",
  text_draft: "Typed draft",
  manual: "Typed by hand",
  raw_conversion: "Smart conversion"
};
const NOT_IMPORTED = new Set(["manual", "text_draft"]);

export function isImported(recipe) {
  if (recipe.recipe_type === "smart_user") return true;
  return Boolean(recipe.source_type) && !NOT_IMPORTED.has(recipe.source_type);
}

export function escapeHTML(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/** An http(s) URL as given, or null: a stored URL is user data, and `javascript:` must not become a link. */
/**
 * `users/{uid}.last_device`, which both apps write when they come to the foreground (#1794): "iOS 26.0.1 · iPhone17,1 ·
 * app 2.1.0 (412)", and when it was written: on a change of device or version, and at most a day apart while the user
 * keeps opening the app (#2354), so it reads as when they were last seen. The direct Firestore fallback hands a live
 * Timestamp, the callable a string.
 */
export function lastDeviceHTML(device) {
  if (!device || typeof device !== "object") {
    return `<span class="text-gray-500 dark:text-gray-400">Not reported yet: the apps write it when opened.</span>`;
  }
  const platform = { ios: "iOS", android: "Android" }[device.platform] || device.platform || "Unknown OS";
  const parts = [
    [platform, device.os_version].filter(Boolean).join(" "),
    device.device_model,
    device.app_version ? `app ${device.app_version}${device.build_number ? ` (${device.build_number})` : ""}` : null
  ].filter(Boolean);
  const at = device.updated_at && typeof device.updated_at.toDate === "function" ? device.updated_at.toDate() : (device.updated_at ? new Date(device.updated_at) : null);
  const when = at && !isNaN(at.getTime())
    ? `<span class="ml-0.5 text-gray-500 dark:text-gray-400">last seen ${escapeHTML(at.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }))}</span>`
    : "";
  const logo = platformLogo(device.platform, "size-4 flex-none");
  return `<span class="inline-flex flex-wrap items-center gap-x-1.5">${logo}<span>${escapeHTML(parts.join(" · "))}</span>${when}</span>`;
}

export function safeUrl(value) {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

function formatDate(iso, withTime = true) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, withTime
    ? { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric" });
}

/** "Tue, Oct 7" for a plan's YYYY-MM-DD, read as a calendar day rather than UTC midnight. */
export function formatPlanDay(ymd) {
  if (typeof ymd !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "No date";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function formatAmount(item) {
  if (item.secondary_amount_text) return escapeHTML(item.secondary_amount_text);
  if (item.amount === null || item.amount === undefined) return "—";
  const amount = Number.isInteger(item.amount) ? item.amount : Math.round(item.amount * 100) / 100;
  return escapeHTML(`${amount}${item.unit ? ` ${item.unit}` : ""}`);
}

function emptyRow(text) {
  return `<p class="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center ${MUTED} dark:border-white/15">${text}</p>`;
}

function table(headers, rowsHTML) {
  return `
    <div class="relative overflow-x-auto">
      <table class="${TABLE.table}">
        <thead><tr>${headers.map((h) => `<th scope="col" class="${TABLE.th}">${h}</th>`).join("")}</tr></thead>
        <tbody class="${TABLE.tbody}">${rowsHTML}</tbody>
      </table>
    </div>`;
}

// Cells that wrap, unlike TABLE.td, since titles and notes can be long.
const TD = "px-3 py-3 text-sm text-gray-500 dark:text-gray-400";
const TD_STRONG = "px-3 py-3 text-sm font-medium text-gray-900 dark:text-white";

/** Recipe titles by id, from the plan and the family's recipes, to name a grocery item's recipes. */
export function recipeTitles(details) {
  const titles = {};
  (details.recipes || []).forEach((r) => { if (r.recipe_id && r.title) titles[r.recipe_id] = r.title; });
  (details.meal_plan?.recipes || []).forEach((r) => { if (r.recipe_id && r.title) titles[r.recipe_id] = r.title; });
  (details.meal_plan?.staged?.recipes || []).forEach((r) => { if (r.recipe_id && r.title) titles[r.recipe_id] = r.title; });
  return titles;
}

/** The counts on the tabs. */
export function tabCounts(details) {
  const items = details.groceries?.items || [];
  return {
    plan: details.meal_plan?.recipes?.length || 0,
    groceries: items.filter((i) => !i.is_completed).length,
    recipes: (details.recipes || []).length,
    imports: (details.import_jobs || []).length + (details.recipe_imports || []).length,
    household: (details.members || []).length
  };
}

/** A meal's own leftovers (#1758): none set follows the household's, 0 is off. */
function mealLeftovers(value) {
  if (value === null || value === undefined) return "Household";
  return value === 0 ? "Off" : String(value);
}

/** A family recipe's title as a button that opens it (#1841); familyCard.js binds `data-family-recipe`. */
function recipeButtonHTML(recipeId, title) {
  return `<button type="button" data-family-recipe="${escapeHTML(recipeId)}" data-family-recipe-title="${escapeHTML(title || "")}" class="text-left font-medium text-gray-900 hover:text-paprika-600 hover:underline dark:text-white dark:hover:text-paprika-400">${escapeHTML(title || "Untitled")}</button>`;
}

function planTableHTML(entries, familyRecipeIds) {
  return table(["Day", "Recipe", "Servings", "Leftovers", "Status"], entries.map((entry) => `
    <tr>
      <td class="${TD} whitespace-nowrap">${escapeHTML(formatPlanDay(entry.scheduled_date))}</td>
      <td class="${TD_STRONG}">
        ${familyRecipeIds.has(entry.recipe_id) ? recipeButtonHTML(entry.recipe_id, entry.title) : escapeHTML(entry.title || "Untitled")}
        ${familyRecipeIds.has(entry.recipe_id) ? `<span class="ml-1.5 ${BADGE.paprika}">Family recipe</span>` : ""}
        <div class="${MONO} mt-0.5 font-normal break-all">${escapeHTML(entry.recipe_id || "no id")}</div>
      </td>
      <td class="${TD}">${entry.servings ?? "—"}</td>
      <td class="${TD}">${escapeHTML(mealLeftovers(entry.leftovers))}</td>
      <td class="${TD}">${entry.is_cooked
        ? `<span class="${BADGE.green}"><span aria-hidden="true" class="${BADGE_DOT.green}"></span>Cooked</span>`
        : `<span class="${BADGE.gray}">Not cooked</span>`}</td>
    </tr>`).join(""));
}

export function planTabHTML(details) {
  const plan = details.meal_plan || {};
  const familyRecipeIds = new Set((details.recipes || []).map((r) => r.recipe_id));
  const facts = [
    ["Confirmed", formatDate(plan.confirmed_at)],
    ["Plan ends", formatDate(plan.end_date, false)],
    ["Next plan generated", formatDate(plan.next_generation_date, false)],
    ["Last auto-generated", formatDate(plan.last_auto_generated_at)],
    ["Auto-schedule", plan.auto_schedule
      ? (plan.auto_schedule.enabled
        ? `On, ${plan.auto_schedule.meals_per_week ?? "?"} dinners every ${plan.auto_schedule.cadence_days ?? "?"} days`
        : "Off")
      : "Not set"]
  ];
  const recipes = plan.recipes || [];
  return `
    <dl class="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
      ${facts.map(([label, value]) => `<div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">${label}</dt><dd class="mt-0.5 text-sm text-gray-900 dark:text-white">${escapeHTML(value)}</dd></div>`).join("")}
    </dl>
    ${plan.legacy_ids_only ? `<p class="mt-4 rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-200">Only the old <code class="font-mono text-xs">active_meal_plan_recipe_ids</code> list is set, so there are no titles or dates.</p>` : ""}
    <div class="mt-4">${recipes.length ? planTableHTML(recipes, familyRecipeIds) : emptyRow("No active meal plan.")}</div>
    ${plan.draft_recipe_count ? `<p class="mt-3 ${MUTED}">${plan.draft_recipe_count} recipe${plan.draft_recipe_count === 1 ? "" : "s"} in an unconfirmed draft.</p>` : ""}
    ${plan.staged ? `
      <h4 class="mt-6 text-sm font-semibold text-gray-900 dark:text-white">Staged next plan</h4>
      <p class="mt-0.5 ${MUTED}">Generated ${escapeHTML(formatDate(plan.staged.generated_at))}${plan.staged.staged_until ? `, becomes active ${escapeHTML(formatDate(plan.staged.staged_until))}` : ""}.</p>
      <div class="mt-3">${plan.staged.recipes.length ? planTableHTML(plan.staged.recipes, familyRecipeIds) : emptyRow("The staged plan has no recipes.")}</div>` : ""}
  `;
}

export function groceriesTabHTML(details) {
  const groceries = details.groceries;
  if (!groceries) return emptyRow("No grocery list yet (nothing at <code class=\"font-mono text-xs\">groceries/current</code>).");
  const titles = recipeTitles(details);
  const items = groceries.items || [];
  const open = items.filter((i) => !i.is_completed).length;
  const rows = items.map((item) => {
    const recipes = item.recipe_ids.map((id) => escapeHTML(titles[id] || id)).join(", ");
    return `
      <tr class="${item.is_completed ? "opacity-60" : ""}">
        <td class="${TD_STRONG}">
          <span class="${item.is_completed ? "line-through" : ""}">${escapeHTML(item.name || "Unnamed")}</span>
          ${item.is_staple ? `<span class="ml-1.5 ${BADGE.blue}">Staple</span>` : ""}
          ${item.note ? `<div class="mt-0.5 text-xs font-normal text-gray-500 dark:text-gray-400">${escapeHTML(item.note)}</div>` : ""}
        </td>
        <td class="${TD} whitespace-nowrap">${formatAmount(item)}</td>
        <td class="${TD}">${escapeHTML(item.category || "—")}</td>
        <td class="${TD} min-w-48">${recipes || (item.added_by_name ? `Added by ${escapeHTML(item.added_by_name)}` : "Added by hand")}</td>
        <td class="${TD} whitespace-nowrap">${item.is_completed
          ? `<span class="${BADGE.green}">Checked</span>${item.checked_at ? `<div class="mt-0.5 text-xs">${escapeHTML(formatDate(item.checked_at))}</div>` : ""}`
          : `<span class="${BADGE.gray}">To buy</span>`}</td>
      </tr>`;
  }).join("");
  return `
    <p class="${MUTED}">${open} to buy, ${items.length - open} checked. Updated ${escapeHTML(formatDate(groceries.updated_at))}.${groceries.has_completed_pantry_check ? " Pantry check done." : ""}</p>
    <div class="mt-4">${items.length ? table(["Item", "Amount", "Aisle", "For", "Status"], rows) : emptyRow("The list is empty.")}</div>
  `;
}

const OPEN_IMPORT = new Set(["processing", "waiting_slot", "waiting_on_other_run", "waiting_for_device"]);

/** A draft's row action (#1607): convert it, from its link when it has one, or say a conversion is under way. */
function convertCellHTML(r, url, converting) {
  if (r.recipe_type === "smart_user") return "";
  if (converting.has(r.recipe_id)) {
    return `<span class="${BADGE.yellow}"><span aria-hidden="true" class="${BADGE_DOT.yellow}"></span>Converting</span>`;
  }
  const id = escapeHTML(r.recipe_id);
  const title = escapeHTML(r.title || "Untitled");
  return `<div class="flex flex-col items-end gap-1">
    <button type="button" data-family-convert="${id}" data-family-convert-from="auto" data-family-convert-title="${title}" data-family-convert-link="${url ? "1" : ""}" class="${BUTTON.small} whitespace-nowrap">${solidIcon("sparkles", "size-4 text-paprika-600")}Convert to Smart</button>
    ${url ? `<button type="button" data-family-convert="${id}" data-family-convert-from="text" data-family-convert-title="${title}" class="${BUTTON.link} text-xs whitespace-nowrap">or from its text</button>` : ""}
  </div>`;
}

/** A draft the card can convert now: not Smart already, and no conversion of it under way. */
function canConvert(r, converting) {
  return Boolean(r.recipe_id) && r.recipe_type !== "smart_user" && !converting.has(r.recipe_id);
}

export function recipesTabHTML(details) {
  const recipes = details.recipes || [];
  if (!recipes.length) return emptyRow("No recipes of their own (nothing in <code class=\"font-mono text-xs\">user_recipes_summary</code>).");
  const imported = recipes.filter(isImported).length;
  const converting = new Set((details.recipe_imports || [])
    .filter((imp) => imp.source_recipe_id && OPEN_IMPORT.has(imp.status))
    .map((imp) => imp.source_recipe_id));
  const convertible = recipes.filter((r) => canConvert(r, converting)).length;
  const rows = recipes.map((r) => {
    const url = safeUrl(r.full_url);
    const source = SOURCE_LABELS[r.source_type] || r.source_type || "Unknown";
    // Ticked drafts convert together (#2471), each from its link when it has one
    const select = canConvert(r, converting)
      ? checkbox(`data-family-select="${escapeHTML(r.recipe_id)}" data-family-select-title="${escapeHTML(r.title || "Untitled")}" aria-label="Select ${escapeHTML(r.title || "Untitled")}"`)
      : "";
    return `
      <tr>
        <td class="${TD} w-8">${select}</td>
        <td class="${TD_STRONG}">
          ${r.recipe_id ? recipeButtonHTML(r.recipe_id, r.title) : escapeHTML(r.title || "Untitled")}
          ${r.recipe_type === "smart_user" ? `<span class="ml-1.5 ${BADGE.paprika}">Smart</span>` : ""}
          ${r.is_shared ? `<span class="ml-1.5 ${BADGE.blue}">Shared</span>` : ""}
          <div class="${MONO} mt-0.5 font-normal break-all">${escapeHTML(r.recipe_id)}</div>
        </td>
        <td class="${TD}">${escapeHTML(source)}${isImported(r) ? "" : ` <span class="text-xs">(not imported)</span>`}</td>
        <td class="${TD} break-all">${url
          ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link}">${escapeHTML(r.source_domain || new URL(url).hostname)}</a>`
          : "—"}</td>
        <td class="${TD} whitespace-nowrap">${escapeHTML(formatDate(r.updated_at, false))}</td>
        <td class="${TD} text-right">${convertCellHTML(r, url, converting)}</td>
      </tr>`;
  }).join("");
  const selectAll = convertible ? checkbox('data-family-select-all aria-label="Select every recipe that can be converted"') : "<span class=\"sr-only\">Select</span>";
  return `
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="${MUTED}">${recipes.length} recipe${recipes.length === 1 ? "" : "s"}, ${imported} imported. From the family's recipes summary, newest first. Open one to see what it holds.</p>
      ${convertible ? `<button type="button" data-family-convert-selected disabled class="${BUTTON.secondary} whitespace-nowrap">${solidIcon("sparkles", "size-4 text-paprika-600")}<span data-family-convert-selected-label>Convert selected</span></button>` : ""}
    </div>
    <div class="mt-4">${table([selectAll, "Recipe", "Source", "Link", "Updated", "<span class=\"sr-only\">Convert</span>"], rows)}</div>
  `;
}

const JOB_BADGES = {
  completed: `<span class="${BADGE.green}"><span aria-hidden="true" class="${BADGE_DOT.green}"></span>Completed</span>`,
  failed: `<span class="${BADGE.red}"><span aria-hidden="true" class="${BADGE_DOT.red}"></span>Failed</span>`,
  processing: `<span class="${BADGE.yellow}"><span aria-hidden="true" class="${BADGE_DOT.yellow}"></span>Processing</span>`,
  queued: `<span class="${BADGE.gray}"><span aria-hidden="true" class="${BADGE_DOT.gray}"></span>Queued</span>`
};

// The new Smart import path (#1558–#1562): an import's status, as its badge
export const IMPORT_BADGES = {
  completed: `<span class="${BADGE.green}"><span aria-hidden="true" class="${BADGE_DOT.green}"></span>Added</span>`,
  failed: `<span class="${BADGE.red}"><span aria-hidden="true" class="${BADGE_DOT.red}"></span>Failed</span>`,
  processing: `<span class="${BADGE.yellow}"><span aria-hidden="true" class="${BADGE_DOT.yellow}"></span>Processing</span>`,
  waiting_slot: `<span class="${BADGE.gray}"><span aria-hidden="true" class="${BADGE_DOT.gray}"></span>Queued</span>`,
  waiting_on_other_run: `<span class="${BADGE.blue}"><span aria-hidden="true" class="${BADGE_DOT.blue}"></span>Another household's run</span>`,
  waiting_for_device: `<span class="${BADGE.paprika}"><span aria-hidden="true" class="${BADGE_DOT.paprika}"></span>Page not read</span>`
};

// An unfinished import the CRM may end (#1847): still and past its queue slot for an hour, as the server checks
const STUCK_AFTER_MS = 60 * 60 * 1000;
const longAgo = (iso, nowMs) => !iso || nowMs - Date.parse(iso) >= STUCK_AFTER_MS;

export function isStuckImport(imp, nowMs = Date.now()) {
  if (!OPEN_IMPORT.has(imp.status)) return false;
  return longAgo(imp.updated_at || imp.created_at, nowMs) && longAgo(imp.slot_at, nowMs);
}

function batchesHTML(batches, imports) {
  if (!batches.length) return "";
  const nowMs = Date.now();
  const rows = batches.map((b) => {
    const state = b.notified_at
      ? `<span class="${BADGE.green}">Push sent</span>`
      : b.settled_at
        ? `<span class="${BADGE.gray}">${b.push_due ? "Push due" : "No push"}</span>`
        : `<span class="${BADGE.yellow}">Not finished</span>`;
    const canPush = !b.notified_at && b.completed > 0 && !b.settled_at;
    // The imports shown are the latest only: an unfinished batch an hour old may hold stuck ones not listed
    const canEnd = !b.settled_at && (imports.some((imp) => imp.batch_id === b.batch_id && isStuckImport(imp, nowMs)) || longAgo(b.created_at, nowMs));
    const who = b.source === "crm" ? `CRM${b.queued_by?.email ? `, ${escapeHTML(b.queued_by.email)}` : ""}` : "App";
    return `
      <tr>
        <td class="${TD} whitespace-nowrap">${escapeHTML(formatDate(b.created_at))}</td>
        <td class="${TD}">${who}</td>
        <td class="${TD} whitespace-nowrap">${b.completed} added, ${b.failed} failed${b.total !== null && b.total !== undefined ? ` of ${b.total}` : ""}</td>
        <td class="${TD}">${state}${b.notify ? "" : ` <span class="text-xs">(push off)</span>`}</td>
        <td class="${TD}"><div class="flex flex-wrap justify-end gap-2"><button type="button" data-family-batch-open="${escapeHTML(b.batch_id)}" class="${BUTTON.small}">${solidIcon("document-text", "size-4 text-gray-400")}Open</button>${canPush ? `<button type="button" data-family-batch-push="${escapeHTML(b.batch_id)}" class="${BUTTON.small}">${solidIcon("paper-airplane", "size-4 text-gray-400")}Send the push now</button>` : ""}${canEnd ? `<button type="button" data-family-end-batch="${escapeHTML(b.batch_id)}" class="${BUTTON.small}">${solidIcon("x-circle", "size-4 text-gray-400")}End stuck imports</button>` : ""}</div></td>
      </tr>`;
  }).join("");
  return `
    <h4 class="text-sm font-semibold text-gray-900 dark:text-white">Batches</h4>
    <p class="mt-1 ${MUTED}">Open a batch for every recipe in it and why each failed. One push per batch, when its last recipe is in. A batch whose import never ends stays unfinished: send its push by hand, then end its stuck imports so the apps stop showing them as processing.</p>
    <div class="mt-2">${table(["Started", "From", "Recipes", "Push", ""], rows)}</div>`;
}

function recipeImportsHTML(imports, limit) {
  if (!imports.length) return "";
  const nowMs = Date.now();
  const rows = imports.map((imp) => {
    const url = safeUrl(imp.url);
    const input = url
      ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link} break-all">${escapeHTML(url)}</a>`
      : escapeHTML(imp.text_start ? `${imp.text_start}…` : "Pasted text");
    const outcome = imp.status === "failed"
      ? `<span class="text-red-700 dark:text-red-400">${escapeHTML(imp.failure_reason || "No reason recorded")}</span>${imp.error_message && imp.error_message !== imp.failure_reason ? `<div class="mt-1 ${MONO} break-words">${escapeHTML(imp.error_message)}</div>` : ""}${url ? `<div class="mt-2"><button type="button" data-family-paste-failed="${escapeHTML(imp.import_id)}" data-family-paste-url="${escapeHTML(url)}" data-family-paste-reason="${escapeHTML(imp.failure_reason || "")}" class="${BUTTON.small}">${solidIcon("clipboard-document", "size-4 text-gray-400")}Paste the recipe instead</button></div>` : ""}`
      : imp.status === "waiting_for_device"
        ? `<button type="button" data-family-paste-import="${escapeHTML(imp.import_id)}" data-family-paste-url="${escapeHTML(url || "")}" class="${BUTTON.small}">${solidIcon("clipboard-document", "size-4 text-gray-400")}Paste the page</button>`
        : `${escapeHTML(imp.recipe_title || "—")}${imp.served_from_cache ? ` <span class="${BADGE.gray}">From the cache</span>` : ""}`;
    const end = isStuckImport(imp, nowMs)
      ? `<div class="mt-2"><button type="button" data-family-end-import="${escapeHTML(imp.import_id)}" class="${BUTTON.small}">${solidIcon("x-circle", "size-4 text-gray-400")}End it</button> <span class="text-xs">Still for over an hour</span></div>`
      : "";
    return `
      <tr>
        <td class="${TD} whitespace-nowrap">${escapeHTML(formatDate(imp.created_at))}</td>
        <td class="${TD}">${IMPORT_BADGES[imp.status] || `<span class="${BADGE.gray}">${escapeHTML(imp.status || "Unknown")}</span>`}</td>
        <td class="${TD} max-w-md">${input}${imp.queued_by?.email ? `<div class="mt-0.5 text-xs">Queued by ${escapeHTML(imp.queued_by.email)}</div>` : ""}</td>
        <td class="${TD}">${outcome}${end}</td>
      </tr>`;
  }).join("");
  return `
    <h4 class="mt-6 text-sm font-semibold text-gray-900 dark:text-white first:mt-0">Imports</h4>
    <p class="mt-1 ${MUTED}">The latest ${imports.length}${imports.length >= (limit || Infinity) ? " (older ones not shown)" : ""}, newest first.</p>
    <div class="mt-2">${table(["Asked", "Status", "Link or text", "Result"], rows)}</div>`;
}

export function importsTabHTML(details) {
  const jobs = details.import_jobs || [];
  const imports = details.recipe_imports || [];
  const batches = details.import_batches || [];
  if (!jobs.length && !imports.length) return emptyRow("No smart imports.");
  const newPath = batchesHTML(batches, imports) + recipeImportsHTML(imports, details.recipe_imports_limit);
  if (!jobs.length) return newPath;
  const rows = jobs.map((job) => {
    const url = safeUrl(job.source_input);
    const input = url
      ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link} break-all">${escapeHTML(url)}</a>`
      : escapeHTML(job.source_input || "—");
    const outcome = job.status === "failed"
      ? `<span class="text-red-700 dark:text-red-400">${escapeHTML(job.failure_reason || job.error_message || "No reason recorded")}</span>${job.failure_reason && job.error_message && job.error_message !== job.failure_reason ? `<div class="mt-0.5 text-xs">${escapeHTML(job.error_message)}</div>` : ""}`
      : escapeHTML(job.recipe_title || "—");
    return `
      <tr>
        <td class="${TD} whitespace-nowrap">${escapeHTML(formatDate(job.created_at))}</td>
        <td class="${TD}">${JOB_BADGES[job.status] || `<span class="${BADGE.gray}">${escapeHTML(job.status || "Unknown")}</span>`}</td>
        <td class="${TD}">${escapeHTML(SOURCE_LABELS[job.source_type] || job.source_type || "—")}</td>
        <td class="${TD} max-w-md">${input}</td>
        <td class="${TD}">${outcome}${job.recipe_id ? `<div class="${MONO} mt-0.5 break-all">${escapeHTML(job.recipe_id)}</div>` : ""}</td>
      </tr>`;
  }).join("");
  return `
    ${newPath}
    ${newPath ? `<h4 class="mt-6 text-sm font-semibold text-gray-900 dark:text-white">Import jobs from earlier app builds</h4>` : ""}
    <p class="${newPath ? "mt-1 " : ""}${MUTED}">The latest ${jobs.length}${jobs.length >= (details.import_jobs_limit || Infinity) ? " (older ones not shown)" : ""}, newest first. A job a member cleared in the app is gone.</p>
    <div class="mt-4">${table(["Started", "Status", "Source", "Input", "Result"], rows)}</div>
  `;
}

export function householdTabHTML(details, currentUid) {
  const people = [
    ...(details.adults || []).map((p) => ({ ...p, kind: "Adult" })),
    ...(details.children || []).map((p) => ({ ...p, kind: "Child" }))
  ];
  const memberRows = (details.members || []).map((m) => `
    <tr>
      <td class="${TD_STRONG}">${escapeHTML(m.name || "No name")}${m.uid === currentUid ? ` <span class="${BADGE.gray}">This user</span>` : ""}${m.test_user ? ` <span class="${BADGE.yellow}" title="Left out of analytics (#1922)">Test user</span>` : ""}${m.created_in === "debug" ? ` <span class="${BADGE.gray}">Debug build</span>` : ""}<div class="${MONO} mt-0.5 font-normal break-all">${escapeHTML(m.uid)}</div></td>
      <td class="${TD}">${m.email ? escapeHTML(m.email) : `<span class="${MUTED}">—</span>`}</td>
      <td class="${TD}">${escapeHTML(m.role || "—")}</td>
      <td class="${TD} min-w-64">${lastDeviceHTML(m.last_device)}</td>
      <td class="${TD} whitespace-nowrap">${escapeHTML(formatDate(m.joined_at, false))}</td>
      <td class="${TD} text-right">${m.uid === currentUid ? "" : `<button type="button" data-family-lookup-uid="${escapeHTML(m.uid)}" class="${BUTTON.small}">${solidIcon("magnifying-glass", "size-4 text-gray-400")}Look up</button>`}</td>
    </tr>`).join("");
  const exclusions = [...(details.strict_exclusions || []), ...(details.custom_excluded_ingredients || [])];
  return `
    <h4 class="text-sm font-semibold text-gray-900 dark:text-white">Accounts</h4>
    <div class="mt-2">${memberRows ? table(["Member", "Email", "Role", "Last device", "Joined", ""], memberRows) : emptyRow("No member documents.")}</div>
    <h4 class="mt-6 text-sm font-semibold text-gray-900 dark:text-white">Who eats</h4>
    <ul class="mt-2 flex flex-wrap gap-2">
      ${people.length ? people.map((p) => `<li class="${BADGE.gray}">${escapeHTML(p.name || "No name")} · ${p.kind}${p.age_group ? `, ${escapeHTML(p.age_group)}` : ""}${p.appetite ? `, ${escapeHTML(p.appetite)} appetite` : ""}</li>`).join("") : `<li class="${MUTED}">Nobody listed.</li>`}
    </ul>
    <dl class="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
      <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Diet</dt><dd class="mt-0.5 text-sm text-gray-900 dark:text-white">${escapeHTML(details.baseline_diet || "—")}</dd></div>
      <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Leftovers</dt><dd class="mt-0.5 text-sm text-gray-900 dark:text-white">${details.leftover_servings ? `${escapeHTML(String(details.leftover_servings))} per meal` : "Off"}</dd></div>
      <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Excluded</dt><dd class="mt-0.5 text-sm text-gray-900 dark:text-white">${escapeHTML(exclusions.join(", ") || "Nothing")}</dd></div>
      <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Created</dt><dd class="mt-0.5 text-sm text-gray-900 dark:text-white">${escapeHTML(formatDate(details.created_at))}</dd></div>
    </dl>
  `;
}

const TAB_BODIES = {
  plan: planTabHTML,
  groceries: groceriesTabHTML,
  recipes: recipesTabHTML,
  imports: importsTabHTML,
  household: householdTabHTML
};

function header(subtitleHTML, actionsHTML = "") {
  return `
    <div class="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
      <div class="flex min-w-0 items-center gap-3">
        <span class="flex size-10 flex-none items-center justify-center rounded-md bg-paprika-50 text-paprika-600 dark:bg-paprika-500/15 dark:text-paprika-300">${icon("shopping-bag", "size-5")}</span>
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">Family</h3>
          <p class="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">${subtitleHTML}</p>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2">${actionsHTML}</div>
    </div>`;
}

export function familyLoadingHTML(familyId) {
  return `
    <div class="${CARD}">
      ${header(`Family <code class="font-mono text-xs">${escapeHTML(familyId)}</code>`)}
      <div class="flex items-center gap-3 px-4 py-6 sm:px-6">
        <span class="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-paprika-600 dark:border-white/20 dark:border-t-paprika-400"></span>
        <p class="${MUTED}">Reading the meal plan, grocery list and recipes…</p>
      </div>
    </div>`;
}

export function familyMessageHTML(familyId, title, text, tone = "gray") {
  const iconHTML = tone === "red"
    ? solidIcon("exclamation-triangle", "size-5 flex-none text-red-500")
    : icon("information-circle", "size-5 flex-none text-gray-400");
  return `
    <div class="${CARD}">
      ${header(familyId ? `Family <code class="font-mono text-xs">${escapeHTML(familyId)}</code>` : "No family")}
      <div class="flex gap-3 px-4 py-5 sm:px-6">
        ${iconHTML}
        <div>
          <p class="text-sm font-medium text-gray-900 dark:text-white">${escapeHTML(title)}</p>
          <p class="mt-1 ${MUTED}">${text}</p>
          ${tone === "red" ? `<button type="button" data-family-retry class="mt-3 ${BUTTON.small}">${solidIcon("arrow-path", "size-4 text-gray-400")}Try again</button>` : ""}
        </div>
      </div>
    </div>`;
}

export function familyCardHTML(details, activeTab = "plan", currentUid = null) {
  const counts = tabCounts(details);
  const tab = TAB_BODIES[activeTab] ? activeTab : "plan";
  const tabs = FAMILY_TABS.map((t) => `
    <button type="button" data-family-tab="${t.id}" aria-pressed="${t.id === tab}" class="${SEGMENT.button} whitespace-nowrap">${t.label}<span class="${SEGMENT.count}">${counts[t.id]}</span></button>`).join("");
  const unread = (details.unread || []).length
    ? `<p class="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not read: ${escapeHTML(details.unread.join(", "))}. Those tabs show nothing rather than the real contents.</p>`
    : "";
  return `
    <div class="${CARD}">
      ${header(
        `${details.name ? `${escapeHTML(details.name)} · ` : ""}<code class="font-mono text-xs">${escapeHTML(details.family_id)}</code>`,
        `<button type="button" data-family-import class="${BUTTON.smallPrimary}">${solidIcon("arrow-down-tray", "size-4")}Import recipes</button>
         <button type="button" data-family-copy-id class="${BUTTON.small}">${solidIcon("clipboard-document", "size-4 text-gray-400")}Copy ID</button>
         <button type="button" data-family-refresh class="${BUTTON.small}">${solidIcon("arrow-path", "size-4 text-gray-400")}Refresh</button>`
      )}
      <div class="flex flex-col gap-4 px-4 py-5 sm:px-6">
        ${unread}
        <div class="overflow-x-auto"><div class="${SEGMENT.group}" role="group" aria-label="Family data">${tabs}</div></div>
        <div data-family-tab-body>${TAB_BODIES[tab](details, currentUid)}</div>
        <p class="text-xs text-gray-400 dark:text-gray-500">Read at ${escapeHTML(formatDate(details.fetched_at || null))}. Only Import recipes, Convert to Smart, a batch's push and ending a stuck import change anything here.</p>
      </div>
    </div>`;
}
